import { useLocalization } from "@/context/localization-context";
import { SeverityIcon } from "../../components/primitives";
import { formatAgo, formatCount, formatMs, useNow } from "../../format";
import type { CamerasSection } from "../../types";
import { BranchFlags, StatTile } from "../pipeline/shared";
import type { CameraRow } from "./model";

/** ThingsBoard list calls slower than this are worth a look */
const TB_SLOW_MS = 3000;

/** Summary strip of the camera tab: watched cameras, ThingsBoard, problems, stuck pizzas, uncovered branches. */
export function CamerasSummary({ section, rows }: { section: CamerasSection; rows: CameraRow[] }) {
  const { t } = useLocalization();
  const now = useNow();

  const watched = rows.filter((r) => r.watched);
  const online = watched.filter((r) => r.kind === "online").length;
  const offline = watched.filter((r) => r.kind === "offline").length;
  const unknown = watched.length - online - offline;
  const assigned = rows.filter((r) => r.camera.assigned).length;
  const critical = rows.filter((r) => r.severity === "critical").length;
  const warning = rows.filter((r) => r.severity === "warning").length;

  // pizza numbers are per branch: count every branch once even when two cameras share it
  const branchStats = new Map<string, NonNullable<CameraRow["camera"]["stats"]>>();
  for (const row of rows) {
    if (row.camera.branch_id && row.camera.stats) branchStats.set(row.camera.branch_id, row.camera.stats);
  }
  let stuck = 0;
  let stuckNoPhoto = 0;
  let uncaptured = 0;
  let captured = 0;
  for (const stats of branchStats.values()) {
    stuck += stats.missing_24h;
    stuckNoPhoto += stats.missing_no_photo_24h ?? stats.missing_24h;
    uncaptured += stats.uncaptured;
    captured += stats.captured_24h;
  }

  const tbMs = section.thingsboard_ms;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <StatTile
          label={t("monitoring.cameras.summary.watched")}
          hint={t("monitoring.cameras.summary.watchedHint")}
          value={t("monitoring.kpi.camerasValue", { online, watched: watched.length })}
          detail={t("monitoring.cameras.summary.watchedDetail", { offline, unknown })}
          tone={offline > 0 ? "nok" : unknown > 0 ? "warn" : "neutral"}
        />
        <StatTile
          label={t("monitoring.cameras.summary.thingsboard")}
          value={formatCount(rows.length)}
          detail={t("monitoring.cameras.summary.thingsboardDetail", {
            assigned,
            unassigned: rows.length - assigned,
          })}
        />
        <StatTile
          label={t("monitoring.cameras.summary.problems")}
          value={formatCount(critical + warning)}
          detail={t("monitoring.cameras.summary.problemsDetail", { critical, warning })}
          tone={critical > 0 ? "nok" : warning > 0 ? "warn" : "neutral"}
        />
        <StatTile
          label={t("monitoring.cameras.summary.captured")}
          value={formatCount(captured)}
          detail={t("monitoring.cameras.summary.capturedDetail", { branches: branchStats.size })}
        />
        <StatTile
          label={t("monitoring.cameras.summary.stuck")}
          hint={t("monitoring.cameras.summary.stuckHint")}
          value={formatCount(stuck)}
          detail={t("monitoring.cameras.summary.stuckDetail", { noPhoto: stuckNoPhoto, uncaptured })}
          tone={stuck > 0 || uncaptured > 0 ? "nok" : "neutral"}
        />
        <StatTile
          label={t("monitoring.cameras.summary.api")}
          hint={t("monitoring.cameras.summary.apiHint")}
          value={formatMs(tbMs)}
          detail={t("monitoring.status.checkedAt", { ago: formatAgo(section.generated_at, now) })}
          tone={typeof tbMs === "number" && tbMs >= TB_SLOW_MS ? "warn" : "neutral"}
        />
      </div>

      {(section.uncovered_branches ?? []).length > 0 && (
        <ul className="flex flex-col divide-y divide-amber-500/15 rounded-md border border-amber-500/35">
          {(section.uncovered_branches ?? []).map((branch) => (
            <li key={branch.id} className="flex items-start gap-2.5 px-3 py-2">
              <SeverityIcon severity="warning" className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="break-words text-sm">
                  {t("monitoring.finding.branch_without_camera", { branch: branch.name || branch.id })}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                  <span className="break-all font-mono">{branch.id}</span>
                </div>
                <BranchFlags acquisition={branch.enable_acquisition} bbTrigger={branch.enable_bb_trigger} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
